<script setup>
const props = defineProps({
  answers: { type: Object, required: true },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['edit', 'submit'])

const prettyAnswers = () => {
  const a = props.answers || {}

  return [
    { label: 'Investment Goal', value: a.goal1Type },
    { label: 'Time Horizon', value: a.goal1Horizon },
    { label: 'Risk Tolerance', value: a.riskTolerance },
    { label: 'Allocation Style', value: a.allocationStyle },
    { label: 'International Exposure', value: a.internationalExposure ? 'Yes' : 'No' },
    { label: 'Exclude Small-Cap', value: a.excludeSmallCap ? 'Yes' : 'No' },
    { label: 'Thematic Funds', value: a.thematicInvesting ? 'Yes' : 'No' }
  ]
}
</script>

<template>
  <div class="sip-review">
    <div class="review-header">
      <h3>Review Your Preferences</h3>
      <p class="review-subtitle">
        Confirm your investment preferences before we generate your personalized SIP portfolio
      </p>
    </div>

    <div class="answers-grid">
      <div
        v-for="(row, idx) in prettyAnswers()"
        :key="idx"
        class="answer-row"
      >
        <div class="answer-label">{{ row.label }}</div>
        <div class="answer-value">{{ row.value || 'Not provided' }}</div>
      </div>
    </div>

    <div class="review-actions">
      <button
        class="btn-secondary"
        @click="emit('edit')"
        :disabled="disabled"
      >
        ← Change Answers
      </button>
      <button
        class="btn-primary"
        @click="emit('submit')"
        :disabled="disabled"
      >
        Get My Portfolio
      </button>
    </div>
  </div>
</template>

<style scoped>
.sip-review {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.review-header {
  text-align: center;
}

.review-header h3 {
  margin: 0 0 8px 0;
  color: var(--an-primary);
  font-size: 1.3rem;
}

.review-subtitle {
  margin: 0;
  color: var(--an-text-secondary);
  font-size: 0.9rem;
}

.answers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.answer-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid var(--an-primary);
}

.answer-label {
  font-size: 0.85rem;
  color: #868e96;
  font-weight: 500;
}

.answer-value {
  font-size: 0.95rem;
  color: var(--an-text-primary);
  font-weight: 600;
}

.review-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
}

.btn-primary,
.btn-secondary {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--an-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a6454;
}

.btn-secondary {
  background: #f1f3f5;
  color: #495057;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
